import ThankYouModal from "./ThankYouModal";
import { useState } from "react";
import { submitLead } from "../lib/formSubmit";
import { toast } from "sonner";
import { Calculator, CheckCircle2, ChevronDown, Loader2, Send } from "lucide-react";
import { fmtUSD, getUtm } from "../data/siteData";
import { useLead } from "../lib/leadContext";
import { BillUploadField, HoneypotField } from "./LeadForm";
import { track } from "../lib/analytics";

const REDUCTIONS = [0, 1.5, 3, 4];

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-[#0B1F44] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-[#0066FF] transition";

export function computeScenarios(rateStr, kwhStr) {
  const r = parseFloat(rateStr);
  const k = parseFloat(kwhStr);
  if (!(r > 0) || r > 60 || !(k > 0)) return null;
  const baseMonthly = (k * r) / 100;
  return REDUCTIONS.map((red) => {
    const newRate = r - red;
    if (newRate < 0) return { red, na: true };
    const monthly = (k * newRate) / 100;
    return {
      red,
      rate: newRate,
      monthly,
      annual: monthly * 12,
      monthlyDiff: baseMonthly - monthly,
      annualDiff: (baseMonthly - monthly) * 12,
      na: false,
    };
  });
}

export default function RateImpactCalculator() {
  const { lead, update, billFile, setBillFile } = useLead();
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [loadedAt] = useState(Date.now());

  const results = lead.calcResults || null;

  const calculate = () => {
    const scenarios = computeScenarios(lead.supplyRate, lead.monthlyKwh);
    if (!scenarios) {
      setError("Enter a supply rate above 0¢/kWh and a monthly usage above 0 kWh.");
      return;
    }
    setError("");
    update({ calcResults: scenarios });
    track("rate_impact_calculated", { rate: lead.supplyRate, kwh: lead.monthlyKwh });
    setTimeout(() => document.getElementById("rate-impact-results")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 150);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("I consent to BlueSettle contacting me about my rate review.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("source", "calculator");
      fd.append("business_name", lead.businessName || "");
      fd.append("contact_name", lead.contactName || "");
      fd.append("email", lead.email || "");
      fd.append("_replyto", lead.email || "");
      fd.append("phone", lead.phone || "");
      fd.append("consent", String(consent));
      fd.append("supply_rate_cents", lead.supplyRate || "");
      fd.append("monthly_kwh", lead.monthlyKwh || "");
      fd.append("calc_scenarios", JSON.stringify(lead.calcResults || []));
      Object.entries(getUtm()).forEach(([k, v]) => fd.append(k, v));
      fd.append("form_elapsed", String((Date.now() - loadedAt) / 1000));
      fd.append("website", e.target.website?.value || "");
      if (billFile) fd.append("bill", billFile);
      await submitLead(fd);
      track("lead_submitted", { source: "calculator" });
      setDone(true);
      toast.success("Thank you! Your request has been submitted.");
    } catch (err) {
      toast.error(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="rate-calculator" className="py-16 md:py-24 bg-[#F4F8FC]">
      <ThankYouModal open={done} onClose={() => setDone(false)} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0066FF]">What would a lower kwh rate mean for your bill?</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F44]">
            Rate Impact Calculator
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Enter your current electricity supply rate and monthly usage to see the dollar impact of potential rate reductions.
          </p>
        </div>

        <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6 sm:p-9 shadow-[0_10px_30px_-5px_rgba(11,31,68,0.08)]" data-testid="rate-calculator">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
            <div>
              <label htmlFor="rc-rate" className="text-sm font-semibold text-[#0B1F44]">Current supply rate (¢/kWh)</label>
              <input
                id="rc-rate" type="number" step="0.1" min="0.1" max="60" placeholder="e.g. 11.4"
                className={`${inputCls} mt-2`}
                value={lead.supplyRate || ""}
                onChange={(e) => update({ supplyRate: e.target.value, calcResults: null })}
                data-testid="calc-rate-input"
              />
            </div>
            <div>
              <label htmlFor="rc-kwh" className="text-sm font-semibold text-[#0B1F44]">Monthly consumption (kWh)</label>
              <input
                id="rc-kwh" type="number" min="1" placeholder="e.g. 50,000"
                className={`${inputCls} mt-2`}
                value={lead.monthlyKwh || ""}
                onChange={(e) => update({ monthlyKwh: e.target.value, calcResults: null })}
                data-testid="calc-kwh-input"
              />
            </div>
          </div>

          {error && <p className="mt-4 text-sm font-medium text-rose-500" role="alert" data-testid="calc-error">{error}</p>}

          <button
            onClick={calculate}
            data-testid="calc-calculate-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-bold px-8 py-3.5 text-sm sm:text-base transition-all shadow-[0_6px_20px_rgba(0,102,255,0.35)]"
          >
            <Calculator size={18} />
            Calculate Rate Impact
          </button>

          {results && (
            <div id="rate-impact-results" className="mt-9">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {results.map((s, i) => (
                  <div
                    key={s.red}
                    data-testid={`calc-card-${s.red}`}
                    className="animate-fade-up rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {s.red === 0 ? "Current Rate · Baseline" : `${s.red}¢ Reduction`}
                    </p>
                    {s.na ? (
                      <p className="mt-4 text-sm font-semibold text-slate-400 leading-relaxed">
                        N/A — reduction exceeds your current rate
                      </p>
                    ) : (
                      <>
                        <p className="mt-3 font-display text-3xl font-extrabold text-[#0B1F44]">
                          {s.rate.toFixed(1)}¢<span className="text-base font-semibold text-slate-400">/kWh</span>
                        </p>
                        <dl className="mt-4 space-y-2 text-sm">
                          <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Monthly supply</dt>
                            <dd className="font-semibold text-[#0B1F44]">{fmtUSD(s.monthly)}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Annual supply</dt>
                            <dd className="font-semibold text-[#0B1F44]">{fmtUSD(s.annual)}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Monthly diff.</dt>
                            <dd className={`font-semibold ${s.red === 0 ? "text-slate-400" : "text-emerald-600"}`}>
                              {s.red === 0 ? "—" : `${fmtUSD(s.monthlyDiff)} saved`}
                            </dd>
                          </div>
                        </dl>
                        <div className={`mt-4 rounded-lg px-3 py-2.5 text-center ${s.red === 0 ? "bg-slate-50" : "bg-emerald-50"}`}>
                          {s.red === 0 ? (
                            <span className="text-sm font-semibold text-slate-400">Baseline</span>
                          ) : (
                            <span className="font-display text-lg font-extrabold text-emerald-600" data-testid={`calc-annual-saved-${s.red}`}>
                              {fmtUSD(s.annualDiff)} <span className="text-sm font-bold">saved / year</span>
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <p className="mt-5 text-xs text-slate-400 leading-relaxed max-w-3xl">
                These are potential rate scenarios for illustration only. They are not actual supplier
                quotes, market forecasts, or guaranteed savings. Available rates depend on your market,
                usage profile, and contract terms.
              </p>

              {!done && !showForm && (
                <button
                  onClick={() => { setShowForm(true); track("rate_impact_cta_clicked"); }}
                  data-testid="calc-find-rate-btn"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0B1F44] hover:bg-[#0066FF] text-white font-bold px-8 py-4 text-base transition-all"
                >
                  Find Me a Lower Rate
                  <ChevronDown size={18} />
                </button>
              )}

              {done ? (
                <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8 text-center" data-testid="calc-lead-success">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white">
                    <CheckCircle2 size={26} className="text-emerald-600" />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-[#0B1F44]">Request received</h3>
                  <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                    Thanks{lead.contactName ? `, ${lead.contactName.split(" ")[0]}` : ""}. A BlueSettle specialist will review supplier options for your
                    usage profile and reach out after reviewing your request.
                  </p>
                </div>
              ) : showForm && (
                <form onSubmit={submit} className="animate-fade-up mt-8 rounded-xl border border-[#0066FF]/25 bg-[#F4F8FC] p-6 sm:p-8" data-testid="calc-lead-form">
                  <HoneypotField />
                  <h3 className="font-display text-lg font-bold text-[#0B1F44]">Get your personalized rate review</h3>
                  <p className="mt-1.5 text-sm text-slate-500">
                    Please double check your contact information. We will send the rate review to your email. 
                  </p>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input required placeholder="Business name *" className={inputCls} value={lead.businessName || ""} onChange={(e) => update({ businessName: e.target.value })} data-testid="calc-form-business-name" />
                    <input required placeholder="Contact name *" className={inputCls} value={lead.contactName || ""} onChange={(e) => update({ contactName: e.target.value })} data-testid="calc-form-contact-name" />
                    <input required type="email" placeholder="Email address *" className={inputCls} value={lead.email || ""} onChange={(e) => update({ email: e.target.value })} data-testid="calc-form-email" />
                    <input type="tel" placeholder="Phone number (optional)" className={inputCls} value={lead.phone || ""} onChange={(e) => update({ phone: e.target.value })} data-testid="calc-form-phone" />
                  </div>
                  <div className="mt-5">
                    <BillUploadField file={billFile} setFile={setBillFile} testid="calc-form-bill-upload" />
                    <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                      This helps us be more accurate with your rate comparision.
                    </p>
                  </div>
                  <label className="mt-5 flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-4 cursor-pointer" data-testid="calc-form-consent">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#0066FF]" required />
                    <span className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      I consent to BlueSettle contacting me by email or phone regarding my rate review
                      request. My information is never sold, and this request does not authorize any
                      supplier switch or contract.
                    </span>
                  </label>
                  <button type="submit" disabled={submitting} data-testid="calc-form-submit-btn"
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 text-white font-bold px-8 py-4 text-base transition-all shadow-[0_8px_30px_rgba(0,102,255,0.35)]">
                    {submitting ? <Loader2 size={19} className="animate-spin" /> : <Send size={18} />}
                    {submitting ? "Packaging your request..." : "Request My Lower Rate"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
